import { createTheme, Theme } from '@mui/material/styles';
import { typography, componentOverrides, fontSizeScale } from './tokens';

// ─── Types ────────────────────────────────────────────────────────────────────

type UserRole = 'expert' | 'client' | 'admin';

interface RoleColorScheme {
  primary: { main: string; light: string; dark: string; contrastText: string };
  secondary: { main: string; light: string; dark: string; contrastText: string };
  background: { default: string; paper: string };
  sidebar: { background: string; color: string; hover: string };
}

// ─── Color schemes per role ───────────────────────────────────────────────────

const roleColorSchemes: Record<UserRole, RoleColorScheme> = {
  expert: {
    primary:    { main: '#1976d2', light: '#42a5f5', dark: '#1565c0', contrastText: '#ffffff' },
    secondary:  { main: '#dc004e', light: '#ff5983', dark: '#9a0036', contrastText: '#ffffff' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    sidebar:    { background: '#1976d2', color: '#ffffff', hover: 'rgba(255,255,255,0.1)' },
  },
  client: {
    primary:    { main: '#2e7d32', light: '#4caf50', dark: '#1b5e20', contrastText: '#ffffff' },
    secondary:  { main: '#f57c00', light: '#ffb74d', dark: '#e65100', contrastText: '#ffffff' },
    background: { default: '#f1f8e9', paper: '#ffffff' },
    sidebar:    { background: '#2e7d32', color: '#ffffff', hover: 'rgba(255,255,255,0.1)' },
  },
  admin: {
    primary:    { main: '#7b1fa2', light: '#9c27b0', dark: '#4a148c', contrastText: '#ffffff' },
    secondary:  { main: '#ff6f00', light: '#ffb74d', dark: '#e65100', contrastText: '#ffffff' },
    background: { default: '#faf5ff', paper: '#ffffff' },
    sidebar:    { background: '#7b1fa2', color: '#ffffff', hover: 'rgba(255,255,255,0.1)' },
  },
};

const defaultColorScheme = roleColorSchemes.expert;

// ─── Theme factory ────────────────────────────────────────────────────────────

export const createRoleTheme = (
  userRole?: UserRole | null,
  mode: 'light' | 'dark' = 'light',
  fontScale: 'normal' | 'compact' = 'normal',
): Theme => {
  const scheme = (userRole && roleColorSchemes[userRole]) || defaultColorScheme;

  return createTheme({
    typography: { ...typography, fontSize: fontSizeScale[fontScale] },
    components: componentOverrides,
    mixins: {
      toolbar: {
        minHeight: 52,
        '@media (min-width: 600px)': { minHeight: 52 },
      },
    },
    palette: {
      mode,
      primary: scheme.primary,
      secondary: scheme.secondary,
      ...(mode === 'light' ? { background: scheme.background } : {}),
      sidebar: scheme.sidebar,
    } as any,
  });
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const getSidebarColors = (userRole?: UserRole | null) =>
  ((userRole && roleColorSchemes[userRole]) || defaultColorScheme).sidebar;

// ─── MUI theme augmentation ───────────────────────────────────────────────────

declare module '@mui/material/styles' {
  interface Palette {
    sidebar: { background: string; color: string; hover: string };
  }
  interface PaletteOptions {
    sidebar?: { background: string; color: string; hover: string };
  }
}
